import {
  addDdocToIndexdb,
  getExistingDocOnIndexeddb,
  modifyUserCacheData,
} from '@/db/queries';
import { FileTypeEnum, PublishStatus, TGunUserNode } from '@/types';
import { getDdocOnGunByIdViaWorker, isNullish } from './gun-utils';
import { UserCache } from '@/db/db';
import { KeyStoreInstance } from './identity-utils/key-store';
import {
  decryptFile,
  encryptFile,
  generateFileMetadata,
  getIPFSAsset,
  handleOnChainPublishing,
  jsonToFile,
  resolveReceiptAndParseLog,
} from './file-utils';
import { ARCH_VERSION, FILE_SOURCE_MAP } from './constants';
import { addFileTransaction, editFileTransaction } from './portal-utils';
import { fromUint8Array } from 'js-base64';
import { Hex } from 'viem';
import { getOwnerLockFromMetadata } from '@/components/ddoc-editor/editor-utils';
import { WorkerInstance } from './worker-utils/worker-instance';
import { IDdocIndexerResponse } from '@/services/get-ddocs-by-portal-address';
import { getContractFileData, PortalContractEvent } from './contract-utils';
import { generateRandomAESKey, generateRandomNonce } from './crypto-utils';
import { attemptTitleDecryption } from '@/components/sidebar/list-item-utils';
import { captureException } from '@sentry/nextjs';
import { FILE_TRX_TIMEOUT } from './account-utils';
import { generateDocWsKey } from './socket-client-utils';

export const parseCommentKey = (commentKey: any) => {
  if (!commentKey) return commentKey;
  let key = '';
  try {
    key = JSON.parse(new TextDecoder().decode(commentKey));
  } catch (error: any) {
    key = new TextDecoder().decode(commentKey);
  }
  return key;
};

export const validateContentHash = async (
  contentHash: string,
  fileKey: any
) => {
  try {
    const result = await decryptFile(fileKey, contentHash);
    return !!result.file;
  } catch (error) {
    return false;
  }
};

export const getContentFromGun = async (ddocId: string) => {
  const userNode = KeyStoreInstance.getGunUserNode();
  if (!userNode) {
    throw new Error('Cannot get content without user node');
  }

  const details = await getDdocOnGunByIdViaWorker(userNode, ddocId);
  if (!details?.content)
    throw new Error('Cannot republish doc with empty content');
  return { content: details?.content, title: details?.title };
};

export const publishContentOnIpfs = async (content: string) => {
  if (!content) throw new Error('Cannot migrate doc with invalid content');
  const contentFile = jsonToFile(
    { file: content, source: FILE_SOURCE_MAP.DDOC },
    fromUint8Array(generateRandomNonce())
  );
  const { fileKey, encryptedFile } = await encryptFile(contentFile);
  const hash = await WorkerInstance.uploadToIPFS(
    encryptedFile,
    contentFile.name,
    []
  );
  return { hash, fileKey };
};

export const validateDdocIdFromOnchainMetadata = async (
  fileId: number,
  portalAddressFromUserCache: string,
  ddocId: string,
  fileKey: any
) => {
  let isInvalidPortalOrFileId = false;
  try {
    const { metadataIPFSHash, contentIPFSHash } = await getContractFileData(
      KeyStoreInstance.getPortalAddress(),
      fileId
    );
    const response = await getIPFSAsset({ ipfsHash: metadataIPFSHash });

    const metadata = response?.data;

    if (metadata.ddocId !== ddocId) {
      throw new Error('invalid metadata');
    }

    return { metadata, isInvalidPortalOrFileId, contentIPFSHash };
  } catch (error) {
    isInvalidPortalOrFileId = true;
    const { metadataIPFSHash, contentIPFSHash } = await getContractFileData(
      portalAddressFromUserCache,
      fileId
    );
    const response = await getIPFSAsset({ ipfsHash: metadataIPFSHash });

    const metadata = response?.data;
    if (metadata.ddocId !== ddocId) {
      throw new Error('invalid metadata');
    }

    const isValidContentHash = await validateContentHash(
      contentIPFSHash,
      fileKey
    );
    if (isValidContentHash) {
      return { metadata, isInvalidPortalOrFileId, contentIPFSHash };
    } else {
      const { content: contentFromGun } = await getContentFromGun(ddocId);
      const { hash, fileKey: _fileKey } = await publishContentOnIpfs(
        contentFromGun as any
      );
      return {
        metadata,
        isInvalidPortalOrFileId,
        contentIPFSHash: hash,
        fileKey: _fileKey,
      };
    }
  }
};

export function isTypedArrayEmpty(typedArray: Uint8Array) {
  return typedArray.length === 0 || typedArray.every((value) => value === 0);
}

export const getOrGenerateCommentKey = async (ownerLock: any) => {
  const ownerPrivateKey = KeyStoreInstance.getDecryptionKey('owner');
  const decryptedChatKey = await WorkerInstance.decryptRSA(
    ownerLock.lockedChatKey,
    ownerPrivateKey
  );

  if (!isTypedArrayEmpty(decryptedChatKey)) {
    const key = parseCommentKey(decryptedChatKey);
    return key;
  }

  const newCommentKey = await generateRandomAESKey();

  return newCommentKey;
};

const migrateIndexerDocToNewArch = async (
  doc: UserCache,
  docItemOnIndexer: IDdocIndexerResponse,
  walletAddress: Hex,
  userNode?: TGunUserNode
) => {
  const { ddocId, linkKeyNonce, linkKey } = doc;

  const localDoc = await getExistingDocOnIndexeddb(ddocId);
  const ownerLock = await getOwnerLockFromMetadata(docItemOnIndexer);

  // if file is already on local db, publish directly from local db
  if (localDoc) {
    if (!localDoc.commentKey) {
      const chatKey = await getOrGenerateCommentKey(ownerLock);
      localDoc.commentKey = chatKey;
    }
    const resp = await handleOnChainPublishing(walletAddress, localDoc);

    if (!resp) throw new Error('Failed to publish doc from local db');
    return;
  }

  const ownerPrivateKey = KeyStoreInstance.getDecryptionKey('owner');

  const decryptedFileKey = await WorkerInstance.decryptRSA(
    ownerLock.lockedFileKey,
    ownerPrivateKey
  );

  const decodedFileKey = JSON.parse(
    new TextDecoder().decode(decryptedFileKey)
  ) as { key: string; iv: string; authTag: string };

  const { encryptedSecretKey, nonce, secretKey, hasLinkKeyChanged } =
    await WorkerInstance.generateEncryptionMaterial({
      ddocId,
      linkKey,
      linkKeyNonce,
    });

  let fileKey = decodedFileKey;
  const rawLinkKey = encryptedSecretKey;
  const fileID = docItemOnIndexer.fileId;
  const {
    isInvalidPortalOrFileId,
    contentIPFSHash,
    fileKey: newFileKey,
  } = await validateDdocIdFromOnchainMetadata(
    fileID,
    doc.portalAddress,
    ddocId,
    fileKey
  );
  const chatKey = await getOrGenerateCommentKey(ownerLock);

  if (isInvalidPortalOrFileId && newFileKey) {
    fileKey = newFileKey;
  }

  if (!doc.title && walletAddress && userNode) {
    try {
      doc.title = await attemptTitleDecryption(doc, walletAddress, userNode);
    } catch (error) {
      captureException(error);
      console.error('Error attempting title decryption', error);
    }
  }

  const fileMetadata = await generateFileMetadata(
    docItemOnIndexer.resolvedMetadata.size,
    fileKey,
    { ddocId, title: doc.title || 'Untitled', commentKey: chatKey },
    secretKey,
    nonce,
    rawLinkKey,
    '2',
    generateDocWsKey() // generate RTC key for migrating doc
  );

  const metadataHash = await WorkerInstance.uploadToIPFS(
    fileMetadata,
    doc.title + '.metadata.json',
    [FILE_SOURCE_MAP.DDOC]
  );
  if (!isInvalidPortalOrFileId) {
    const txHash = await editFileTransaction({
      contractAddress: KeyStoreInstance.getPortalAddress(),
      contentIpfsHash: contentIPFSHash,
      metadataIpfsHash: metadataHash,
      fileType: FileTypeEnum.PRIVATE,
      fileId: docItemOnIndexer.fileId,
    });
    await modifyUserCacheData(doc.ddocId, walletAddress, {
      pendingTxnHash: txHash,
    });

    const parsedLog = await resolveReceiptAndParseLog(
      txHash,
      PortalContractEvent.EditedFile,
      doc.ddocId,
      walletAddress,
      FILE_TRX_TIMEOUT
    );

    // @ts-ignore
    const onChainFileId = Number(parsedLog[0].args.fileId);

    await modifyUserCacheData(doc.ddocId, walletAddress, {
      onChainFileId,
      linkKey: encryptedSecretKey,
      linkKeyNonce: fromUint8Array(nonce),
      publishStatus: PublishStatus.Published,
      archVersion: ARCH_VERSION,
      commentKey: chatKey,
      hasLinkKeyChanged: hasLinkKeyChanged && !isNullish(doc.onChainFileId),
    });
  } else {
    const txHash = await addFileTransaction({
      contractAddress: KeyStoreInstance.getPortalAddress(),
      contentIpfsHash: contentIPFSHash,
      metadataIpfsHash: metadataHash,
      fileType: FileTypeEnum.PRIVATE,
    });
    const parsedLog = await resolveReceiptAndParseLog(
      txHash,
      PortalContractEvent.AddedFile,
      doc.ddocId,
      walletAddress,
      FILE_TRX_TIMEOUT
    );

    // @ts-ignore
    const onChainFileId = Number(parsedLog[0].args.fileId);

    await modifyUserCacheData(doc.ddocId, walletAddress, {
      onChainFileId,
      linkKey: rawLinkKey,
      linkKeyNonce: fromUint8Array(nonce),
      publishStatus: PublishStatus.Published,
      archVersion: ARCH_VERSION,
      commentKey: chatKey,
      pendingTxnHash: null,
      hasLinkKeyChanged: hasLinkKeyChanged && !isNullish(doc.onChainFileId),
    });
  }
};

export const migrateDocToNewArch = async (
  doc: UserCache,
  walletAddress: Hex,
  docItemOnIndexer: IDdocIndexerResponse
) => {
  const userNode = KeyStoreInstance.getGunUserNode();

  if (docItemOnIndexer) {
    await migrateIndexerDocToNewArch(
      doc,
      docItemOnIndexer,
      walletAddress,
      userNode
    );
    return;
  }

  if (userNode) {
    const gunDocDetails = await getGunDocDetails(doc.ddocId, userNode);
    if (
      !gunDocDetails ||
      !gunDocDetails?.content ||
      gunDocDetails.isDeleted ||
      gunDocDetails.markedForDeletion
    )
      return;
    const resp = await handleOnChainPublishing(walletAddress, gunDocDetails);
    if (!resp) throw new Error('Failed to publish doc from gun');
    return;
  }

  throw new Error(
    'Failed to migrate doc, doc not found on gun, local, or indexer'
  );
};

const getGunDocDetails = async (ddocId: string, userNode: TGunUserNode) => {
  const localDoc = await getExistingDocOnIndexeddb(ddocId);
  if (localDoc) return localDoc;

  const gunDocDetails = await getDdocOnGunByIdViaWorker(userNode, ddocId);
  if (gunDocDetails) {
    await addDdocToIndexdb({ ...gunDocDetails, ddocId: ddocId });
    return gunDocDetails;
  }
};
